import { match } from "ts-pattern"
import { addPreCopyButtons, markdownToHTML } from "../markdown"
import htmx from "htmx.org"
import { ChatMessageManager, Params as ChatMessageManagerParams, RelatedEvent } from "../chunk-manager"
import { AssistantResponseAggregateChunkSseEvent, AssistantResponseChunkSseEvent, AssistantResponseEndSseEvent, ChatTitleGeneratedSseEvent, SSE_EVENTS } from "$types/SSE_EVENTS"
import { CHAT_MESSAGE_STATUS } from "$dbSchemas/ChatsMessages"
import { CHAT_MESSAGE_ERROR_TEXT } from "$constants/messages"
import { CHAT_PROMPT_FORM_ID } from "$constants/elementIds"

declare global {
  interface Window {
    chatMessageManager: ChatMessageManager | null
    chatSSE: EventSource | null
    initSSE: () => void
    setChatMessageManager: (params: ChatMessageManagerParams | null) => void
    onChatFormAfterSubmit: (chatId: number, action: "enable" | "disable") => void
    onInputPromptInput: (event: InputEvent) => void
    onInputPromptKeydown: (event: KeyboardEvent) => void
  }
}

const toggleChatTabsDisabledStatus = (force: boolean) => {
  const container = document.querySelector("[data-chat-tabs-container]")
  if (!container) {
    return
  }

  const chatTabs = document.querySelector("#chat-tabs")

  if (chatTabs) {
    const tabItems = chatTabs.querySelectorAll(".buttonTabs__item")
    tabItems.forEach(tab => {
      tab.classList.toggle("is-disabled", force)

      const link = tab.querySelector(".buttonTabs__link")

      if (link) {
        link.toggleAttribute("disabled", force)
      }
    })
  }

  container.classList.toggle("opacity-50", force)
  container.classList.toggle("pointer-events-none", force)
}

const toggleDropdownDisabledStatus = (chatId: number, force: boolean) => {
  const dropdown = document.querySelector(`[data-dropdown-toggle="chat-options-dropdown-${chatId}"]`)
  if (!dropdown) {
    return
  }

  dropdown.classList.toggle("opacity-50", force)
  dropdown.classList.toggle("pointer-events-none", force)
}

const toggleFormDisabledStatus = (force: boolean) => {
  const form = document.querySelector("#chat-prompt-form")
  if (!form) {
    return
  }

  const button = form.querySelector("button[type=submit]")
  if (!button) {
    return
  }

  button.classList.toggle("btn--disabled", force)
  button.toggleAttribute("disabled", force)
  button.classList.toggle("btn--loading", force)
}

const disableChatButtons = (chatId: number) => {
  toggleChatTabsDisabledStatus(true)
  toggleDropdownDisabledStatus(chatId, true)
  toggleFormDisabledStatus(true)
}

const enableChatButtons = (chatId: number) => {
  toggleChatTabsDisabledStatus(false)
  toggleDropdownDisabledStatus(chatId, false)
  toggleFormDisabledStatus(false)
}

const getChunkFromEvent = (event: RelatedEvent) => {
  return match(event.type)
    .with(SSE_EVENTS.ASSISTANT_RESPONSE_CHUNK, () => {
      const sseEvent = event as AssistantResponseChunkSseEvent
      return {
        chunk: { content: sseEvent.data.content, index: sseEvent.data.index },
        completeText: null,
      }
    })
    .with(SSE_EVENTS.ASSISTANT_RESPONSE_AGGREGATE_CHUNK, () => {
      const sseEvent = event as AssistantResponseAggregateChunkSseEvent
      return {
        chunk: null,
        completeText: { content: sseEvent.data.content, fromIndex: sseEvent.data.fromIndex, toIndex: sseEvent.data.toIndex },
      }
    })
    .with(SSE_EVENTS.ASSISTANT_RESPONSE_END, () => {
      const sseEvent = event as AssistantResponseEndSseEvent
      return {
        chunk: null,
        completeText: { content: sseEvent.data.content, fromIndex: 0, toIndex: sseEvent.data.content?.length ?? 0 },
      }
    })
    .exhaustive()
}

const handleFollowUpQuestions = (messageId: number, forceLoading = false, content?: string) => {
  let target: HTMLElement | null = document.querySelector(`.chat__message__couple > [data-follow-up-questions-container]:has(+[data-response-container="${messageId}"])`)
  let swap: string = "outerHTML"

  const couple = target?.closest(".chat__message__couple")

  if (target && forceLoading) {
    couple?.removeAttribute("data-follow-up-questions-loading")
    return false
  }

  if (!target && forceLoading) {
    target = document.querySelector(`[data-response-container="${messageId}"]`)
    swap = "beforebegin"
  }
  
  if (!target) {
    couple?.removeAttribute("data-follow-up-questions-loading")
    console.warn(`Target element for follow-up questions not found for message ID: ${messageId}`)
    return false
  }

  htmx.ajax("post", "/partials/chats/follow-up-questions", {
    target,
    swap,
    values: {
      messageId, forceLoading,
      content: content || window.chatMessageManager?.context.rawContent || "",
    },
  }).then(() => {
    couple?.removeAttribute("data-follow-up-questions-loading")
  })

  return true
}

const handleNewChunk = (options: { element: HTMLElement, event: RelatedEvent, chatId: number }) => {
  const { element, event, chatId } = options

  const messageId = element.getAttribute("data-message-id")
  if (!messageId) {
    return
  }

  if (!window.chatMessageManager) {
    return
  }

  window.chatMessageManager.addEvent(event)

  disableChatButtons(chatId)

  const { chunk, completeText } = getChunkFromEvent(event)

  const waitingFirstChunk = element.hasAttribute("data-waiting-first-chunk")
  const awaitCompleteText = element.hasAttribute("data-await-complete-text") || (
    waitingFirstChunk && (
      chunk && chunk.index !== 0
    )
  )

  const context = window.chatMessageManager.context

  if (awaitCompleteText) {
    if (completeText) {
      element.innerHTML = markdownToHTML(context.content)
      element.removeAttribute("data-await-complete-text")
      if (waitingFirstChunk) {
        element.removeAttribute("data-waiting-first-chunk")
      }
      element.classList.remove("typing")
      element.setAttribute("data-md", "")
      element.setAttribute("data-message-response", "")

      const couple = element.closest(".chat__message__couple")

      if (couple && !couple.hasAttribute("data-follow-up-questions-loading")) {
        if (context.loadingFollowUpQuestions) {
          couple?.setAttribute("data-follow-up-questions-loading", "")
          handleFollowUpQuestions(parseInt(messageId), true)
        }
      }

      element.setAttribute("data-text", window.chatMessageManager.context.content)
    }
  } else {
    if (waitingFirstChunk) {
      element.removeAttribute("data-waiting-first-chunk")
    }
    element.innerHTML = markdownToHTML(context.content)
    element.setAttribute("data-text", window.chatMessageManager.context.content)

    const couple = element.closest(".chat__message__couple")

    if (couple && !couple.hasAttribute("data-follow-up-questions-loading")) {
      if (context.loadingFollowUpQuestions) {
        couple?.setAttribute("data-follow-up-questions-loading", "")
        handleFollowUpQuestions(parseInt(messageId), true)
      }
    }

    element.classList.remove("typing")
    element.setAttribute("data-md", "")
    element.setAttribute("data-message-response", "")
  }

  addPreCopyButtons()

  const hiddenContainer = element.closest(".chat__message__hidden-container")
  if (!hiddenContainer) {
    return
  }

  const copyButton = hiddenContainer.querySelector(`[data-message-copy-button-response]`)
  if (!copyButton) {
    return
  }

  copyButton.classList.remove("btn--disabled")
  copyButton.removeAttribute("disabled")
}

const handleSuccess = (options: { element: HTMLElement, chatId: number, enableChatButtonsFn?: (chatId: number) => void }) => {
  window.setChatMessageManager(null)
  const { element, chatId } = options
  element.classList.remove("typing")
  element.setAttribute("data-md", "")
  element.setAttribute("data-message-response", "")

  const promptInput = document.querySelector("#input-prompt-inner-container")

  if (promptInput) {
    promptInput.setAttribute("data-message-status", CHAT_MESSAGE_STATUS.SUCCESS)
  }

  const dropdown = document.querySelector(`[data-dropdown-toggle="chat-options-dropdown-${chatId}"]`)

  if (dropdown) {
    const endpoint = dropdown.getAttribute("data-update-endpoint")

    if (endpoint) {
      htmx.ajax("get", endpoint, {})
    }
  }

  if (options.enableChatButtonsFn) {
    options.enableChatButtonsFn(chatId)
  } else {
    enableChatButtons(chatId)
  }
}

const handleError = (options: { element: HTMLElement, chatId: number }) => {
  window.setChatMessageManager(null)
  const { element, chatId } = options
  element.innerHTML = CHAT_MESSAGE_ERROR_TEXT
  element.classList.remove("typing")
  element.classList.add("chat__message__content--error")
  element.setAttribute("data-md", "")

  const hiddenContainer = element.closest(".chat__message__hidden-container")
  if (!hiddenContainer) {
    return
  }

  hiddenContainer.classList.add("chat__message--error")
  const copyButton = hiddenContainer.querySelector(`[data-message-copy-button-response]`)
  if (!copyButton) {
    return
  }

  copyButton.classList.add("btn--disabled")
  copyButton.setAttribute("disabled", "")

  enableChatButtons(chatId)

  const followUpQuestionsContainer = hiddenContainer.querySelector("[data-follow-up-questions-container]")
  if (followUpQuestionsContainer) {
    followUpQuestionsContainer.remove()
  }

  const promptInput = document.querySelector(`#input-prompt-inner-container`) as HTMLTextAreaElement | null

  if (promptInput) {
    promptInput.setAttribute("data-message-status", CHAT_MESSAGE_STATUS.ERROR)
    
    const windowWidth = window.innerWidth
    if (windowWidth > 678) {
      promptInput.focus()
    }
  }

  const form = document.querySelector("#chat-prompt-form")
  if (!form) {
    return
  }

  const button = form.querySelector("button[type=submit]")
  if (!button) {
    return
  }

  button.classList.remove("btn--loading")
  const hasValue = promptInput && promptInput.value.trim().length > 0
  button.toggleAttribute("disabled", !hasValue)
  button.classList.toggle("btn--disabled", !hasValue)

  element.removeAttribute("data-message-loading-response")
}

const getMessageElement = (messageId: number) => {
  const element = document.querySelector(`.chat__message__content[data-message-loading-response][data-message-id="${messageId}"]`) as HTMLElement | null
  if (!element) {
    return null
  }

  return element
}

const getChatIdFromMessageElement = (element: HTMLElement) => {
  const chatId = element.getAttribute("data-chat-id")
  if (!chatId) {
    return null
  }

  return parseInt(chatId)
}

const handleAssistantResponseChunk = (event: AssistantResponseChunkSseEvent) => {
  const {
    messageId
  } = event.data

  const element = getMessageElement(messageId)
  if (!element) {
    return
  }
  const chatId = getChatIdFromMessageElement(element)
  if (!chatId) {
    return
  }

  handleNewChunk({
    element,
    event,
    chatId
  })
}

const handleAssistantResponseAggregateChunk = (event: AssistantResponseAggregateChunkSseEvent) => {
  const {
    messageId
  } = event.data

  const element = getMessageElement(messageId)
  if (!element) {
    return
  }
  const chatId = getChatIdFromMessageElement(element)
  if (!chatId) {
    return
  }

  handleNewChunk({
    element,
    chatId,
    event
  })
}

const handleAssistantResponseEnd = (event: AssistantResponseEndSseEvent) => {
  const {
    messageId,
    status
  } = event.data
  const element = getMessageElement(messageId)
  if (!element) {
    return
  }
  const chatId = getChatIdFromMessageElement(element)
  if (!chatId) {
    return
  }

  const container = element.querySelector(".function-call-loading")
  if (container) {
    container.remove()
  }

  const content = event.data.content || ""

  if (window.chatMessageManager) {
    window.chatMessageManager.addEvent(event)
  }

  if (status === CHAT_MESSAGE_STATUS.ERROR) {
    handleError({
      element,
      chatId
    })
  } else {
    let swapped = false
    let retries = 0
    const followUpInterval = setInterval(() => {
      const couple = element.closest(".chat__message__couple")
      couple?.setAttribute("data-follow-up-questions-loading", "")
      swapped = handleFollowUpQuestions(event.data.messageId, false, content)
      retries += 1
      if (swapped || retries >= 5) {
        clearInterval(followUpInterval)
      }
    }, 1000)
    
    handleSuccess({
      element,
      chatId,
      enableChatButtonsFn: () => {
        const form = document.querySelector("#chat-prompt-form")
        if (!form) {
          return
        }

        const textarea = form.querySelector("textarea[name=inputPrompt]") as HTMLTextAreaElement | null
        if (textarea) {
          const windowWidth = window.innerWidth
          if (windowWidth > 678) {
            textarea.focus()
          }
        }

        const buttonWrapper = form.querySelector("button[type=submit]")?.closest(".btn__wrapper")
        if (!buttonWrapper) {
          return
        }

        const button = buttonWrapper.querySelector("button[type=submit]") as HTMLButtonElement | null
        if (!button) {
          return
        }

        const hasValue = textarea && textarea.value.trim().length > 0
        button.toggleAttribute("disabled", !hasValue)
        button.classList.toggle("btn--disabled", !hasValue)
        buttonWrapper.classList.toggle("btn__wrapper--disabled", !hasValue)
        buttonWrapper.classList.remove("btn--loading")
        button.classList.remove("btn--loading")

        element.removeAttribute("data-message-loading-response")
      }
    })
  }
}

window.onChatFormAfterSubmit = (chatId, action) => {
  const homeViewContainer = document.getElementById("#home-view-container")
  const chatMessagesList = homeViewContainer?.querySelector(".chat__messages__list") as HTMLDivElement
  if (chatMessagesList) {
    chatMessagesList.style.height = ""
  }
  if (action === "enable") {
    enableChatButtons(chatId)
  } else {
    disableChatButtons(chatId)
  }
}

const handleChatTitleGenerated = (event: ChatTitleGeneratedSseEvent) => {
  const { title, chatUUID } = event.data
  const sidebarElement = document.querySelector(`[data-sidebar-chat-title="${chatUUID}"]`) as HTMLAnchorElement | null
  if (!sidebarElement) {
    return
  }

  sidebarElement.textContent = title
  sidebarElement.parentElement?.setAttribute("data-title", title)
}

window.initSSE = () => {
  const html = document.documentElement
  if (!html) {
    return
  }

  const currentUrl = window.location.href
  if (currentUrl.includes("/utility-login")) {
    html.removeAttribute("data-chat-sse-initialized")
  }

  const inputPrompt = document.getElementById(CHAT_PROMPT_FORM_ID) 
  const chatUUID = inputPrompt?.getAttribute("data-chat-uuid")

  if (html.hasAttribute("data-chat-sse-initialized")) {
    const uuid = html.getAttribute("data-chat-sse-initialized") as string
    if (uuid === chatUUID) {
      return
    }
  }

  if (!inputPrompt || !chatUUID) {
    return
  }

  const url = `${window.APP_BASE_URL}/api/chat/open-sse`
  
  if (window.chatSSE) {
    window.chatSSE.close()
  }
  window.chatSSE = new EventSource(`${url}?uuid=${chatUUID}`)

  html.setAttribute("data-chat-sse-initialized", chatUUID)

  window.chatSSE.onerror = (_event) => {
    const html = document.documentElement
    if (!html) {
      return
    }

    html.removeAttribute("data-chat-sse-initialized")
  }

  window.chatSSE.onmessage = (event) => {
    const data = JSON.parse(event.data)

    match(data.type)
      .with(SSE_EVENTS.ASSISTANT_RESPONSE_CHUNK, () => {
        const sseEvent = JSON.parse(event.data) as AssistantResponseChunkSseEvent
        handleAssistantResponseChunk(sseEvent)
      })
      .with(SSE_EVENTS.ASSISTANT_RESPONSE_AGGREGATE_CHUNK, () => {
        const sseEvent = JSON.parse(event.data) as AssistantResponseAggregateChunkSseEvent
        handleAssistantResponseAggregateChunk(sseEvent)
      })
      .with(SSE_EVENTS.ASSISTANT_RESPONSE_END, () => {
        const sseEvent = JSON.parse(event.data) as AssistantResponseEndSseEvent
        handleAssistantResponseEnd(sseEvent)
      })
      .with(SSE_EVENTS.CHAT_TITLE_GENERATED, () => {
        const sseEvent = JSON.parse(event.data) as ChatTitleGeneratedSseEvent
        handleChatTitleGenerated(sseEvent)
      })
      .otherwise(() => {})
  }
}

window.onInputPromptInput = (event: InputEvent) => {
  const target = event.target as HTMLTextAreaElement

  if (!target) {
    return
  }

  const homeViewContainer = target.closest("#home-view-container")
  const chatMessagesList = homeViewContainer?.querySelector(".chat__messages__list") as HTMLDivElement | null
  const conversationPanelContainer = target.closest("#conversation-panel-container")
  const container = target.closest(".input-prompt__container") as HTMLDivElement | null

  if (!container) {
    return
  }

  const windowWidth = window.innerWidth

  if (!chatMessagesList) {
    target.setAttribute("style", "max-height: 96px;")
  } else {
    target.removeAttribute("style")
  }

  target.rows = 1
  target.style.height = "auto"
  target.style.height = target.scrollHeight + "px"
  container.style.height = "fit-content"

  const headerHeight = windowWidth < 678 ? 69 : 104
  const targetIsActive = document.activeElement === target
  const formHeightMin = windowWidth < 678 ? targetIsActive ? 130 : 69 : 56

  if (chatMessagesList) {
    const form = target.closest("form") as HTMLFormElement
    const newChatMessagesListHeight = `calc(var(--viewport-height) - var(--home-page-gap) - var(--footer-height) - ${Math.max(form.clientHeight + 1, formHeightMin)}px - ${headerHeight}px)`
    chatMessagesList.style.height = newChatMessagesListHeight
  }

  const submitButton = document.getElementById("send-message-button") as HTMLButtonElement
  if (!submitButton) {
    return
  }

  const messageStatus: CHAT_MESSAGE_STATUS = target.getAttribute("data-message-status") as CHAT_MESSAGE_STATUS || CHAT_MESSAGE_STATUS.SUCCESS
  if (![CHAT_MESSAGE_STATUS.PROCESSING, CHAT_MESSAGE_STATUS.PENDING].includes(messageStatus)) {
    const hasValue = target.value.trim().length > 0
    submitButton.toggleAttribute("disabled", !hasValue)
    submitButton.classList.toggle("btn--disabled", !hasValue)
  }
}

window.onInputPromptKeydown = (event: KeyboardEvent) => {
  const form = document.getElementById("chat-prompt-form") as HTMLFormElement
  const inputPrompt = event.target as HTMLTextAreaElement

  const messageStatus: CHAT_MESSAGE_STATUS = inputPrompt.getAttribute("data-message-status") as CHAT_MESSAGE_STATUS || CHAT_MESSAGE_STATUS.SUCCESS


  if (!form) {
    return
  }

  if (
    event.key === "Enter" 
    && !([CHAT_MESSAGE_STATUS.PROCESSING, CHAT_MESSAGE_STATUS.PENDING]).includes(messageStatus)
  ) {
    if (!event.shiftKey && window.innerWidth > 678) {
      if (inputPrompt.value.trim().length) {
        event.preventDefault()
        htmx.trigger(form, "submit", {})
      }
    }
  }
}
